import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown } from "lucide-react";
import { usePerspective, PERSPECTIVES } from "../context/PerspectiveContext";

const OPTIONS = [
  { value: "build-scale", label: "Build & Scale" },
  { value: "transform-optimize", label: "Transform & Optimize" },
  { value: "explore", label: "Explore Prospera" },
];

const MobileSwitcher = ({ perspective, setPerspective }) => (
  <div data-testid="perspective-switcher-mobile" className="border-b border-black/5 py-4">
    <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-ink/40">
      Your perspective
    </p>
    <div className="flex flex-col gap-1">
      {OPTIONS.map((o) => (
        <button
          key={o.value}
          data-testid={`perspective-option-mobile-${o.value}`}
          onClick={() => setPerspective(o.value)}
          className={`flex items-center justify-between py-2 text-left font-mono text-xs uppercase tracking-[0.15em] ${
            perspective === o.value ? "text-cobalt" : "text-ink/60"
          }`}
        >
          {o.label}
          {perspective === o.value && <Check size={14} />}
        </button>
      ))}
    </div>
  </div>
);

const PathwaySwitcher = ({ scrolled = false, variant = "desktop" }) => {
  const { perspective, setPerspective } = usePerspective();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  if (!perspective) return null;

  if (variant === "mobile") {
    return <MobileSwitcher perspective={perspective} setPerspective={setPerspective} />;
  }

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="perspective-switcher-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Change your perspective"
        className={`flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] transition-colors ${
          scrolled ? "text-ink/70 hover:text-cobalt" : "text-white/70 hover:text-white"
        }`}
      >
        <span className={`hidden 2xl:inline ${scrolled ? "text-ink/40" : "text-white/40"}`}>
          Your perspective:
        </span>
        {PERSPECTIVES[perspective].label}
        <ChevronDown
          size={13}
          className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="perspective-switcher-menu"
            role="menu"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="absolute right-0 top-full z-50 mt-4 w-64 border border-black/10 bg-white p-1.5 shadow-2xl"
          >
            {OPTIONS.map((o) => (
              <button
                key={o.value}
                role="menuitem"
                data-testid={`perspective-option-${o.value}`}
                onClick={() => {
                  setPerspective(o.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between px-4 py-3 text-left font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-200 ${
                  perspective === o.value
                    ? "bg-cobalt/5 text-cobalt"
                    : "text-ink/70 hover:bg-black/[0.03] hover:text-ink"
                }`}
              >
                {o.label}
                {perspective === o.value && <Check size={14} />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PathwaySwitcher;
